import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  PLAN_TYPE_OPTIONS,
  WAITING_PERIOD_TYPE_OPTIONS,
  WaitingPeriodType,
} from "@shared/schema";
import {
  isFsaPlanType,
  isVolumeRatedPlanType,
} from "@shared/schemas/planTypes";
import { US_STATES } from "@/lib/usStates";
import {
  RateTableEditor,
  emptyRateTable,
  isRateTableComplete,
  missingTierLabels,
  isContributionsComplete,
  missingContributionTierLabels,
  type WizardRateMode,
  type WizardRateTable,
} from "@/components/migration/RateTableEditor";
import { RateModelToggle } from "@/components/migration/RateModelToggle";
import {
  MatchPanel,
  type LibraryMatchStatus,
  type LibraryMatchCandidate,
} from "@/components/migration/MatchPanel";
import { MatchRateTablePreview } from "@/components/migration/MatchRateTablePreview";
import { BrowseAlternatesList } from "@/components/migration/BrowseAlternatesList";
import { CarrierPicker } from "@/components/migration/CarrierPicker";
import { CarrierDialog } from "@/components/ui/plans/AddCarrierDialog";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Bus,
  Check,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Eye,
  Heart,
  HeartPulse,
  Loader2,
  PawPrint,
  Pencil,
  Plus,
  Smile,
  Trash2,
  Wrench,
  X,
} from "lucide-react";

type PlanTier =
  | "employee_only"
  | "employee_spouse"
  | "employee_children"
  | "family";

const TIERS: { key: PlanTier; label: string }[] = [
  { key: "employee_only", label: "EE Only" },
  { key: "employee_spouse", label: "EE + Spouse" },
  { key: "employee_children", label: "EE + Children" },
  { key: "family", label: "Family" },
];

interface EditablePlan {
  displayName: string;
  adminName: string;
  easePlanType: string;
  internalPlanType: string | null;
  carrierName: string;
  /** FK link to the master `carriers` table when the broker explicitly
   *  picked / confirmed a carrier via CarrierPicker. confirmPlans uses
   *  this to bypass the legacy name-lookup (which silently
   *  auto-creates duplicate carrier rows on typos). */
  carrierId?: number | null;
  effectiveStart: string | null;
  effectiveEnd: string | null;
  policyNumber: string;
  subgroupNumber: string;
  ageDetermination?: string;
  deductionCycle?: string;
  waitingPeriodAmount?: number | null;
  waitingPeriodUnit?: WaitingPeriodType | null;
  ratingRegion?: string | null;
  contributions?: { tier: PlanTier; employer: number | null; employee: number | null }[];
  /** Phase B: rate-shape mode + table the broker entered. */
  rateMode?: WizardRateMode;
  rateTable?: WizardRateTable;
  /** Phase B Phase 2: library match state for age-rated plans. */
  matchStatus?: LibraryMatchStatus;
  matchedWbId?: string | null;
  matchedWbName?: string | null;
  candidates?: LibraryMatchCandidate[];
  /** Library-cached rates payload (jsonb array of {age, rate, tobacco?}).
   *  Attached locally so the Found-state "Show rate table" disclosure can
   *  render without a second round-trip. NOT persisted server-side — the
   *  PATCH validator strips unknown fields back out; the next reload will
   *  show the same Found status but rates undefined until a re-match. */
  libraryRates?: Array<{ age: number; ageRange: string; rate: number; tobacco?: boolean }> | null;
  /** Which (region, month) the libraryRates actually represent, and whether
   *  they came from the cache or a live Word & Brown fetch. Surfaced in the
   *  Found panel so the broker can see exactly what they're importing. When
   *  ratesSource is "unavailable", ratesReason explains why no rates loaded. */
  ratesRatingAreaId?: string | null;
  ratesEffectiveMonth?: string | null;
  ratesSource?: "cache" | "live" | "unavailable" | null;
  ratesReason?: string | null;
  /** Phase B: broker marked this plan as "skip the library, add rates later". */
  deferred?: boolean;
  /** Broker opted out of library matching and is filling in the age × tier
   *  rate matrix by hand. When true, the edit form renders RateTableEditor
   *  with the age-band matrix instead of MatchPanel, and the readiness gate
   *  checks `rateTable` completeness instead of `matchStatus === "found"`. */
  manualEntry?: boolean;
}

// RATE_MODE_OPTIONS removed — replaced by `<RateModelToggle>` which owns its
// own segment labels (Age rated · Tier rates · Per benefit amount).

/** First day of the effective-start month (YYYY-MM-01), or null. Matches the
 *  server's month granularity so changing the day doesn't trigger a refetch. */
function effectiveMonthKey(effectiveStart?: string | null): string | null {
  if (!effectiveStart) return null;
  const d = new Date(effectiveStart);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

interface Carrier {
  name: string;
  planTypes: string[];
}

interface PlansStepProps {
  jobId: number;
  carriers: Carrier[];
  initialPlans: EditablePlan[];
  /**
   * Parsed enrollments (read-only here). Used to derive a rate preview and
   * detect age-rated plans without waiting for confirm.
   */
  parsedEnrollments?: ParsedEnrollmentLite[];
  isConfirmed: boolean;
  canConfirm: boolean;
  blockedReason?: string | null;
  /** PlansStep no longer owns the Confirm CTA — the wizard footer does.
   *  Callback fires whenever the in-card readiness changes so the parent
   *  can drive the footer's enabled/disabled state and tooltip reason. */
  onGateChange?: (gate: { ready: boolean; reason: string }) => void;
  /** When true, hide the "Edit plans" reopen affordance. The whole wizard
   *  is locked post-completion; brokers edit from the client profile. */
  isLocked?: boolean;
  /** Client's home state. Drives the "Rating region" picker — when a
   *  state has seeded rating areas (CA today, others later), we render a
   *  Select; otherwise we fall back to the existing free-text Input. */
  clientState?: string | null;
  /** Numeric clients.id, used by the inline state picker that lets a
   *  broker set the client's home state from the wizard when it's
   *  missing. Null when we don't have a linked client (orphan job). */
  clientId?: number | null;
}

interface ParsedEnrollmentLite {
  status: string;
  planDisplayName: string;
  easePlanType: string;
  coverageTier: string;
  totalRate: number | null;
  employeeRate: number | null;
  spouseRate: number | null;
  childrenRate: number | null;
}

interface PlanRateAgg {
  /** First non-zero monthlyRate seen (matches confirmPlans heuristic) */
  monthlyRate: number;
  /** Distinct non-zero monthlyRate values — >2 ⇒ likely age-rated */
  distinctRates: Set<number>;
  enrollCount: number;
}

function emptyPlan(): EditablePlan {
  return {
    displayName: "",
    adminName: "",
    easePlanType: "",
    internalPlanType: null,
    carrierName: "",
    effectiveStart: null,
    effectiveEnd: null,
    policyNumber: "",
    subgroupNumber: "",
    waitingPeriodAmount: null,
    waitingPeriodUnit: WaitingPeriodType.DAYS,
    ratingRegion: null,
    contributions: TIERS.map((t) => ({
      tier: t.key,
      employer: null,
      employee: null,
    })),
  };
}

function ensureContribs(
  contribs: EditablePlan["contributions"],
): NonNullable<EditablePlan["contributions"]> {
  const base = TIERS.map((t) => ({
    tier: t.key,
    employer: null as number | null,
    employee: null as number | null,
  }));
  if (!contribs?.length) return base;
  for (const c of contribs) {
    const slot = base.find((b) => b.tier === c.tier);
    if (slot) {
      slot.employer = c.employer ?? null;
      slot.employee = c.employee ?? null;
    }
  }
  return base;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  return s;
}

function fmtWaiting(p: EditablePlan): string {
  if (p.waitingPeriodAmount == null) return "—";
  const unit = p.waitingPeriodUnit ?? WaitingPeriodType.DAYS;
  return `${p.waitingPeriodAmount} ${unit}`;
}

function fmtPlanTypeLabel(internal: string | null): string {
  if (!internal) return "—";
  return (
    PLAN_TYPE_OPTIONS.find((o) => o.value === internal)?.label ?? internal
  );
}

// 1-2 letter initials for the carrier avatar (e.g. "Kaiser Permanente" → "KP",
// "MetLife" → "ME"). First-word / last-word initials for multi-word names;
// first two letters otherwise.
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === "") return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic avatar color per carrier name — stable across renders /
// reloads so the same carrier always gets the same ring tint.
const CARRIER_PALETTE: { ring: string; text: string }[] = [
  { ring: "border-emerald-400", text: "text-emerald-700" },
  { ring: "border-blue-400", text: "text-blue-700" },
  { ring: "border-violet-400", text: "text-violet-700" },
  { ring: "border-amber-500", text: "text-amber-700" },
  { ring: "border-rose-400", text: "text-rose-700" },
  { ring: "border-cyan-400", text: "text-cyan-700" },
  { ring: "border-orange-400", text: "text-orange-700" },
];

function carrierAvatarColor(name: string): { ring: string; text: string } {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return CARRIER_PALETTE[Math.abs(h) % CARRIER_PALETTE.length];
}

// Plan-type → icon + tint colors for the 36×36 tile in each plan card.
// Soft tinted backgrounds with a slightly darker fg, per design tokens.
function planTypeTile(internal: string | null): {
  Icon: typeof Heart;
  bg: string;
  fg: string;
} {
  const t = (internal ?? "").toLowerCase();
  if (t === "health" || t === "medical")
    return { Icon: HeartPulse, bg: "bg-blue-50", fg: "text-blue-600" };
  if (t === "dental")
    return { Icon: Smile, bg: "bg-sky-50", fg: "text-sky-600" };
  if (t === "vision")
    return { Icon: Eye, bg: "bg-violet-50", fg: "text-violet-600" };
  if (t === "fsa_health" || t === "fsa_dependent_care")
    return { Icon: DollarSign, bg: "bg-emerald-50", fg: "text-emerald-600" };
  if (t === "life" || t === "life_basic" || t === "life_voluntary" ||
      t === "voluntary_life" || t === "accidental_death" ||
      t === "add_basic" || t === "add_voluntary")
    return { Icon: Heart, bg: "bg-stone-100", fg: "text-stone-600" };
  if (t === "ltd" || t === "std" || t === "disability")
    return { Icon: Activity, bg: "bg-amber-50", fg: "text-amber-700" };
  if (t === "accident" || t === "critical_illness" || t === "cancer" ||
      t === "hospital_indemnity")
    return { Icon: AlertCircle, bg: "bg-stone-100", fg: "text-stone-600" };
  if (t === "pet")
    return { Icon: PawPrint, bg: "bg-violet-50", fg: "text-violet-600" };
  if (t === "transit")
    return { Icon: Bus, bg: "bg-blue-50", fg: "text-blue-600" };
  return { Icon: Wrench, bg: "bg-stone-100", fg: "text-stone-600" };
}

function fmtUsdShort(n: number): string {
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function PlansStep({
  jobId,
  carriers,
  initialPlans,
  parsedEnrollments = [],
  isConfirmed,
  canConfirm,
  blockedReason,
  onGateChange,
  isLocked,
  clientState,
  clientId,
}: PlansStepProps) {
  const { toast } = useToast();
  const qc = useQueryClient();

  // Rating areas seeded for the client's home state. CA has 19 today;
  // other states return [] until they're seeded — falls back to free
  // text. Just `label` is needed downstream (saved as a plain string).
  const { data: ratingAreaRows = [] } = useQuery<
    Array<{ id: number; label: string; areaNumber: number; state: string }>
  >({
    queryKey: ["/api/rating-areas", { state: clientState }],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/rating-areas?state=${encodeURIComponent(clientState ?? "")}`,
      );
      return await res.json();
    },
    enabled: Boolean(clientState),
  });
  const hasRatingAreas = ratingAreaRows.length > 0;
  const ratingAreaLabels = useMemo(
    () => ratingAreaRows.map((r) => r.label),
    [ratingAreaRows],
  );

  const normalizedInitial = useMemo<EditablePlan[]>(
    () =>
      initialPlans.map((p) => ({
        ...p,
        waitingPeriodAmount: p.waitingPeriodAmount ?? null,
        waitingPeriodUnit: p.waitingPeriodUnit ?? WaitingPeriodType.DAYS,
        ratingRegion: p.ratingRegion ?? null,
        contributions: ensureContribs(p.contributions),
      })),
    [initialPlans],
  );
  // The persisted/saved version of plans
  const [plans, setPlans] = useState<EditablePlan[]>(normalizedInitial);
  // Which card is currently in edit mode (-1 = none)
  const [editingIdx, setEditingIdx] = useState<number>(-1);
  // Working draft of the card being edited — lives only in the UI
  const [draft, setDraft] = useState<EditablePlan | null>(null);

  // Re-sync when the server-side plansData changes
  useEffect(() => {
    setPlans(normalizedInitial);
    setEditingIdx(-1);
    setDraft(null);
  }, [normalizedInitial]);

  // Auto-matching state. We track a "we've kicked off auto-match for this
  // server payload" flag so the effect below doesn't re-fire after we
  // patch results back to the server (which would otherwise cycle).
  const [autoMatching, setAutoMatching] = useState(false);
  const [autoMatchedKey, setAutoMatchedKey] = useState<string | null>(null);
  // Which collapsed card has its rate-table preview disclosed. Only one at
  // a time — keeps the plan list scannable. Ephemeral; not persisted.
  const [openRatesIdx, setOpenRatesIdx] = useState<number | null>(null);
  // Same pattern for the "Browse alternates" carrier-catalog disclosure
  // (the inline replacement for the old WrongMatchPopover).
  const [openBrowseIdx, setOpenBrowseIdx] = useState<number | null>(null);
  // Carrier filter — when set, the per-plan list below renders only plans
  // for that carrier. Driven by tapping a card in the carrier grid; tap the
  // same card again to clear. Pure UI state, not persisted.
  const [selectedCarrierName, setSelectedCarrierName] = useState<string | null>(null);
  // Controls the "+ Add carrier" dialog (CarrierDialog from
  // @/components/ui/plans/AddCarrierDialog — full CRUD over /api/carriers).
  const [carrierDialogOpen, setCarrierDialogOpen] = useState(false);

  // Mirror confirmPlans.ts aggregation: per (planDisplayName + easePlanType)
  // → per coverageTier, derive a preview of what would land in planRates on
  // confirm. Lets us render a rate summary on each card pre-confirm.
  const ratePreviewByPlanKey = useMemo(() => {
    const out = new Map<string, Map<PlanTier, PlanRateAgg>>();
    for (const e of parsedEnrollments) {
      if (e.status !== "enrolled") continue;
      const tier = e.coverageTier as PlanTier;
      if (!TIERS.some((t) => t.key === tier)) continue;
      const planKey = `${e.planDisplayName.trim().toLowerCase()}|${e.easePlanType.trim().toLowerCase()}`;
      const monthlyRate =
        (e.employeeRate ?? 0) + (e.spouseRate ?? 0) + (e.childrenRate ?? 0);
      let byTier = out.get(planKey);
      if (!byTier) {
        byTier = new Map();
        out.set(planKey, byTier);
      }
      let agg = byTier.get(tier);
      if (!agg) {
        agg = { monthlyRate: 0, distinctRates: new Set(), enrollCount: 0 };
        byTier.set(tier, agg);
      }
      if (agg.monthlyRate === 0 && monthlyRate > 0) agg.monthlyRate = monthlyRate;
      if (monthlyRate > 0) agg.distinctRates.add(monthlyRate);
      agg.enrollCount++;
    }
    return out;
  }, [parsedEnrollments]);

  function previewKey(p: EditablePlan): string {
    return `${p.displayName.trim().toLowerCase()}|${p.easePlanType.trim().toLowerCase()}`;
  }

  function fmtUsd(n: number): string {
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  const savePlans = useMutation({
    mutationFn: async (payload: EditablePlan[]) => {
      const res = await apiRequest(
        "PATCH",
        `/api/migration/jobs/${jobId}/plans`,
        { plans: payload },
      );
      return (await res.json()) as { job: unknown };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
    },
    onError: (err: any) =>
      toast({
        title: "Save failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  /** Library match call. Called when the broker enters age_tier_rates mode,
   *  changes rating region, or submits a refine query. Result merges back
   *  into the in-progress `draft`. */
  const matchLibrary = useMutation({
    mutationFn: async (vars: {
      planIndex: number;
      query?: string | null;
      ratingAreaId?: string | null;
      effectiveStart?: string | null;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/migration/jobs/${jobId}/plans/match-library`,
        {
          planIndex: vars.planIndex,
          query: vars.query ?? null,
          ratingAreaId: vars.ratingAreaId ?? null,
          effectiveStart: vars.effectiveStart ?? null,
        },
      );
      return (await res.json()) as {
        status: LibraryMatchStatus;
        candidates: LibraryMatchCandidate[];
        matchedWbId: string | null;
        matchedWbName: string | null;
        rates?: Array<{ age: number; ageRange: string; rate: number; tobacco?: boolean }> | null;
        ratesRatingAreaId?: string | null;
        ratesEffectiveMonth?: string | null;
        ratesSource?: "cache" | "live" | "unavailable" | null;
        ratesReason?: string | null;
      };
    },
    onSuccess: (data) => {
      // Only merge into draft if we're still editing the same plan.
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              matchStatus: data.status,
              matchedWbId: data.matchedWbId,
              matchedWbName: data.matchedWbName,
              candidates: data.candidates,
              libraryRates:
                data.status === "found" ? data.rates ?? null : null,
              ratesRatingAreaId:
                data.status === "found" ? data.ratesRatingAreaId ?? null : null,
              ratesEffectiveMonth:
                data.status === "found" ? data.ratesEffectiveMonth ?? null : null,
              ratesSource:
                data.status === "found" ? data.ratesSource ?? null : null,
              ratesReason:
                data.status === "found" ? data.ratesReason ?? null : null,
            }
          : prev,
      );
    },
    onError: (err: any) =>
      toast({
        title: "Library lookup failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  /** Helper: fire the match call against the currently-editing plan, using the
   *  draft's display name as the search query (so broker edits to the name are
   *  reflected without saving first). */
  function fireMatch(opts?: {
    query?: string | null;
    ratingAreaId?: string | null;
    effectiveStart?: string | null;
  }) {
    if (editingIdx < 0 || !draft) return;
    // Optimistically flip to searching so the panel renders the spinner.
    setDraft((prev) => (prev ? { ...prev, matchStatus: "searching" } : prev));
    matchLibrary.mutate({
      planIndex: editingIdx,
      query: opts?.query ?? draft.displayName ?? null,
      ratingAreaId: opts?.ratingAreaId ?? draft.ratingRegion ?? null,
      effectiveStart: opts?.effectiveStart ?? draft.effectiveStart ?? null,
    });
  }

  // Auto-refetch the match when area OR effective month changes while editing.
  // Debounced + deduped on (area, month); seeded in startEdit so opening a card
  // doesn't re-fire.
  const lastMatchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (editingIdx < 0 || !draft) return;
    if (draft.rateMode !== "age_tier_rates") return; // only age-rated use library rates
    const area = draft.ratingRegion?.trim() || null;
    if (!area) return; // no region → matcher can't fetch rates anyway
    const key = `${area}|${effectiveMonthKey(draft.effectiveStart) ?? ""}`;
    if (key === lastMatchKeyRef.current) return;
    // Show the loading state immediately so the debounce window isn't silent.
    setDraft((prev) =>
      prev && prev.matchStatus !== "searching"
        ? { ...prev, matchStatus: "searching" }
        : prev,
    );
    const handle = setTimeout(() => {
      lastMatchKeyRef.current = key;
      fireMatch();
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingIdx, draft?.rateMode, draft?.ratingRegion, draft?.effectiveStart]);

  // Background auto-match on page load. For every age-rated plan that
  // hasn't been matched yet, fire the library matcher in sequence, then
  // batch-persist all results in one PATCH. Means the broker doesn't
  // have to open each card just to discover whether it auto-matched —
  // the summary strip and per-card RATES line are accurate at first
  // paint.
  //
  // Gates:
  //   - Skip when already in edit mode (broker is making changes).
  //   - Skip when there are no plans yet.
  //   - Skip when we've already auto-matched for this server payload
  //     (keyed by jobId + plan count + ids of plans missing a match).
  //   - For states with no seeded rating areas, mark all age-rated plans
  //     as `no_library_data` + deferred locally and persist — no network
  //     calls, broker is auto-unblocked.
  useEffect(() => {
    if (editingIdx >= 0) return;
    if (!normalizedInitial || normalizedInitial.length === 0) return;
    if (clientState && ratingAreaRows == null) return; // wait for rating-areas query

    // Mirror startEdit()'s auto-suggest: a plan that hasn't been opened
    // yet may have no rateMode. If the parser saw >2 distinct enrollment
    // rates per tier and the plan type isn't volume-rated, treat it as
    // age-rated for auto-match purposes.
    const effectiveMode = (p: EditablePlan): WizardRateMode | undefined => {
      if (p.rateMode) return p.rateMode;
      if (isVolumeRatedPlanType(p.internalPlanType)) return "volume_per_thousand";
      const preview = ratePreviewByPlanKey.get(previewKey(p));
      const ageRated =
        preview &&
        Array.from(preview.values()).some((agg) => agg.distinctRates.size > 2);
      return ageRated ? "age_tier_rates" : "tier_rates";
    };

    const candidates = normalizedInitial
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => {
        if (p.matchStatus !== undefined) return false;
        if (p.deferred) return false;
        // FSA plans don't have rates — the library has no FSA carriers
        // and matching them produces a useless not_found that the broker
        // would have to dismiss on every plan card. Skip entirely.
        if (isFsaPlanType(p.internalPlanType)) return false;
        if (effectiveMode(p) !== "age_tier_rates") return false;
        // Phase A: age-rated medical plans are priced geographically (ACA
        // rating formula = age + tobacco + family size + rating area). We
        // can't pull correct rates without knowing the region, so skip
        // auto-match until the broker picks one. The card surfaces a
        // "Pick a region" prompt; matching kicks off after they save.
        if (!p.ratingRegion?.trim()) return false;
        return true;
      });

    if (candidates.length === 0) return;

    // Stable key for this batch — avoids re-running for the same payload
    // after we PATCH results back.
    const key = `${jobId}:${normalizedInitial.length}:${candidates
      .map((c) => c.i)
      .join(",")}`;
    if (autoMatchedKey === key || autoMatching) return;
    setAutoMatchedKey(key);

    // No-library-data short-circuit: state has no seeded rating areas.
    if (!hasRatingAreas) {
      const next = normalizedInitial.map((p, i) => {
        const isCand = candidates.some((c) => c.i === i);
        if (!isCand) return p;
        return {
          ...p,
          rateMode: p.rateMode ?? ("age_tier_rates" as WizardRateMode),
          matchStatus: "no_library_data" as LibraryMatchStatus,
          deferred: true,
        };
      });
      setPlans(next);
      savePlans.mutate(next);
      return;
    }

    setAutoMatching(true);
    (async () => {
      const updates = new Map<number, Partial<EditablePlan>>();
      for (const { p, i } of candidates) {
        try {
          const res = await apiRequest(
            "POST",
            `/api/migration/jobs/${jobId}/plans/match-library`,
            {
              planIndex: i,
              query: p.displayName ?? null,
              ratingAreaId: p.ratingRegion ?? null,
            },
          );
          const data = (await res.json()) as {
            status: LibraryMatchStatus;
            candidates: LibraryMatchCandidate[];
            matchedWbId: string | null;
            matchedWbName: string | null;
            rates?: Array<{ age: number; ageRange: string; rate: number; tobacco?: boolean }> | null;
          };
          updates.set(i, {
            rateMode: p.rateMode ?? ("age_tier_rates" as WizardRateMode),
            matchStatus: data.status,
            matchedWbId: data.matchedWbId,
            matchedWbName: data.matchedWbName,
            candidates: data.candidates,
            libraryRates:
              data.status === "found" ? data.rates ?? null : null,
          });
        } catch {
          // Don't toast — failures here are non-blocking, broker can
          // open the card and refine manually.
        }
      }
      if (updates.size > 0) {
        const next = normalizedInitial.map((p, i) =>
          updates.has(i) ? { ...p, ...updates.get(i)! } : p,
        );
        setPlans(next);
        try {
          await savePlans.mutateAsync(next);
        } catch {
          /* savePlans onError already toasts */
        }
      }
      setAutoMatching(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedInitial, hasRatingAreas, jobId, clientState]);

  const reopenPlans = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/migration/jobs/${jobId}/plans/reopen`,
      );
      return (await res.json()) as { job: unknown };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
      toast({
        title: "Plans reopened",
        description: "You can now re-edit and re-confirm.",
      });
    },
    onError: (err: any) =>
      toast({
        title: "Reopen failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  // Inline state-setter — when the client has no home state set, the
  // wizard surfaces a Select banner that PATCHes `clients.state`
  // without forcing the broker out to the client profile page.
  const setHomeState = useMutation({
    mutationFn: async (state: string) => {
      if (!clientId) throw new Error("No linked client to update");
      const res = await apiRequest("PATCH", `/api/clients/${clientId}`, {
        state,
      });
      return (await res.json()) as { id: number; state: string };
    },
    onSuccess: () => {
      // Re-fetch the client (drives `clientState` upstream) and the
      // rating-areas dropdown options for the new state.
      qc.invalidateQueries({ queryKey: [`/api/clients/${clientId}`] });
      qc.invalidateQueries({ queryKey: ["/api/rating-areas"] });
      toast({ title: "Client state updated" });
    },
    onError: (err: any) =>
      toast({
        title: "Couldn't save state",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });
  const [pendingState, setPendingState] = useState<string>("");

  // Collapsed-card match-edit pathway used by the "Wrong match?"
  // popover. Mirrors the in-edit matchLibrary mutation but writes back
  // to the persisted `plans` array instead of the editing draft.
  const [popoverRefiningIdx, setPopoverRefiningIdx] = useState<number | null>(
    null,
  );
  async function applyPlanMatchUpdate(
    idx: number,
    partial: Partial<EditablePlan>,
  ) {
    const next = plans.map((p, i) =>
      i === idx ? { ...p, ...partial } : p,
    );
    setPlans(next);
    try {
      await savePlans.mutateAsync(next);
    } catch {
      /* savePlans onError already toasts */
    }
  }
  /** Fetch the full carrier catalog (every distinct library plan for
   *  this plan's carrier, ranked by similarity to the plan name). Used
   *  by the browse-all-plans popover. Doesn't mutate plan state — caller
   *  decides what to do with the candidates. */
  async function listCarrierLibrary(
    idx: number,
  ): Promise<LibraryMatchCandidate[]> {
    const plan = plans[idx];
    if (!plan) return [];
    const res = await apiRequest(
      "POST",
      `/api/migration/jobs/${jobId}/plans/list-carrier-library`,
      { planIndex: idx },
    );
    const data = (await res.json()) as {
      candidates: LibraryMatchCandidate[];
    };
    return data.candidates ?? [];
  }

  async function runPopoverMatch(idx: number, query: string) {
    const plan = plans[idx];
    if (!plan) return;
    setPopoverRefiningIdx(idx);
    try {
      const res = await apiRequest(
        "POST",
        `/api/migration/jobs/${jobId}/plans/match-library`,
        {
          planIndex: idx,
          query,
          ratingAreaId: plan.ratingRegion ?? null,
        },
      );
      const data = (await res.json()) as {
        status: LibraryMatchStatus;
        candidates: LibraryMatchCandidate[];
        matchedWbId: string | null;
        matchedWbName: string | null;
        rates?: Array<{
          age: number;
          ageRange: string;
          rate: number;
          tobacco?: boolean;
        }> | null;
      };
      await applyPlanMatchUpdate(idx, {
        matchStatus: data.status,
        matchedWbId: data.matchedWbId,
        matchedWbName: data.matchedWbName,
        candidates: data.candidates,
        libraryRates:
          data.status === "found" ? data.rates ?? null : null,
      });
    } catch (err: any) {
      toast({
        title: "Library lookup failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setPopoverRefiningIdx(null);
    }
  }

  function startEdit(idx: number) {
    // Collapse the inline rate-table preview if this card had it open —
    // MatchPanel renders its own disclosure inside the edit form.
    if (openRatesIdx === idx) setOpenRatesIdx(null);
    setEditingIdx(idx);
    const p = plans[idx];
    // Seed the auto-refetch dedup key so opening a card doesn't re-fire.
    const seedArea = p.ratingRegion?.trim() || null;
    lastMatchKeyRef.current = seedArea
      ? `${seedArea}|${effectiveMonthKey(p.effectiveStart) ?? ""}`
      : null;
    // Auto-suggest a rateMode the first time a card is edited:
    //   volume-rated plan type → volume_per_thousand
    //   else if parser saw >2 distinct rates per tier → age_tier_rates
    //   else → tier_rates
    let suggestedMode: WizardRateMode | undefined = p.rateMode;
    if (!suggestedMode && !isFsaPlanType(p.internalPlanType)) {
      // FSA plans have no rate model — skip the auto-suggest entirely.
      // Leaving rateMode undefined tells the edit form to render the
      // FSA-specific informational panel instead of a rate editor.
      if (isVolumeRatedPlanType(p.internalPlanType)) {
        suggestedMode = "volume_per_thousand";
      } else {
        const preview = ratePreviewByPlanKey.get(previewKey(p));
        const ageRated =
          preview &&
          Array.from(preview.values()).some((agg) => agg.distinctRates.size > 2);
        suggestedMode = ageRated ? "age_tier_rates" : "tier_rates";
      }
    }

    // Seed the rate table. For tier_rates, pre-fill any null entries with the
    // auto-derived rates the read-only RATES strip is showing — keeps WYSIWYG
    // between the card view and the edit form. Volume / age_tier modes have
    // no auto-derive available, so they stay empty for the broker to fill.
    // FSA plans have no rateMode → no rate table; carry undefined through.
    let seededRateTable = p.rateTable;
    if (!seededRateTable && suggestedMode) {
      seededRateTable = emptyRateTable(suggestedMode, p.internalPlanType);
    }
    if (suggestedMode === "tier_rates" && seededRateTable) {
      const preview = ratePreviewByPlanKey.get(previewKey(p));
      const existingEntries =
        seededRateTable.mode === "tier_rates" ? seededRateTable.entries : [];
      seededRateTable = {
        mode: "tier_rates",
        entries: TIERS.map((t) => {
          const existing = existingEntries.find((e) => e.tier === t.key);
          if (existing && existing.monthlyRate != null) return existing;
          const auto = preview?.get(t.key);
          return {
            tier: t.key,
            monthlyRate:
              auto && auto.monthlyRate > 0 ? auto.monthlyRate : null,
          };
        }),
      };
    }

    setDraft({
      ...p,
      contributions: ensureContribs(p.contributions),
      rateMode: suggestedMode,
      rateTable: seededRateTable,
    });

    // Library match: when entering age_tier_rates with no prior match state,
    // either fire the matcher (when the state has rating areas seeded) or
    // short-circuit to "no_library_data" + auto-defer.
    if (
      suggestedMode === "age_tier_rates" &&
      p.matchStatus == null &&
      !p.manualEntry
    ) {
      if (!hasRatingAreas) {
        setDraft((prev) =>
          prev
            ? {
                ...prev,
                matchStatus: "no_library_data",
                deferred: true,
                candidates: [],
                matchedWbId: null,
                matchedWbName: null,
                libraryRates: null,
              }
            : prev,
        );
      } else {
        // Defer to a microtask so setDraft above has settled before the
        // mutation reads it. Mutation always sends planIndex=idx + the
        // current draft values via fireMatch().
        queueMicrotask(() => {
          setDraft((prev) =>
            prev ? { ...prev, matchStatus: "searching" } : prev,
          );
          matchLibrary.mutate({
            planIndex: idx,
            query: p.displayName ?? null,
            ratingAreaId: p.ratingRegion ?? null,
          });
        });
      }
    }
  }

  function cancelEdit() {
    setEditingIdx(-1);
    setDraft(null);
  }

  async function saveEdit() {
    if (editingIdx < 0 || !draft) return;
    // Hard validation per rate mode. Phase B Phase 2: age-rated plans are
    // gated on a library match (or explicit defer), not on a populated
    // rateTable — confirmPlans pulls those rows from cached_plan_rates.
    if (!draft.deferred && !isFsaPlanType(draft.internalPlanType)) {
      if (draft.rateMode === "age_tier_rates") {
        if (draft.manualEntry) {
          // Manual age × tier matrix path. Region isn't required (rates
          // are entered by hand, not pulled by region); just check that
          // the broker filled in at least one cell.
          if (!isRateTableComplete(draft.rateTable)) {
            toast({
              title: "Fill in at least one rate before saving",
              description:
                "Add a value to any age × tier cell, or hit Skip · add later if you want to defer.",
              variant: "destructive",
            });
            return;
          }
        } else {
          if (!draft.ratingRegion?.trim()) {
            toast({
              title: "Pick a rating region first",
              description:
                "Age-rated medical plans are priced geographically — premiums vary by rating area. Pick the broker's home region before saving.",
              variant: "destructive",
            });
            return;
          }
          if (draft.matchStatus !== "found") {
            toast({
              title: "Pick a library match before saving",
              description:
                "Age-rated plans need a confirmed library entry. Pick a candidate, refine the search, or switch to tier rates.",
              variant: "destructive",
            });
            return;
          }
        }
      } else if (
        draft.rateMode === "volume_per_thousand" &&
        !isRateTableComplete(draft.rateTable)
      ) {
        toast({
          title: "Enter a rate before saving",
          description:
            'This plan uses "Per benefit amount". At least one rate value must be filled in.',
          variant: "destructive",
        });
        return;
      } else if (
        draft.rateMode === "tier_rates" &&
        !isRateTableComplete(draft.rateTable)
      ) {
        const missing = missingTierLabels(draft.rateTable);
        toast({
          title: "Fill all tier rates before saving",
          description: `Tier rate plans need every coverage tier filled. Missing: ${missing.join(", ")}.`,
          variant: "destructive",
        });
        return;
      }
      // Contributions (employer/employee splits per tier) are advisory —
      // missing splits surface as a non-blocking warning, but the save
      // path doesn't gate on them so brokers can defer contribution entry
      // to the plan-details page post-import.
    }
    const next = plans.map((p, i) => (i === editingIdx ? draft : p));
    try {
      await savePlans.mutateAsync(next);
      setPlans(next);
      setEditingIdx(-1);
      setDraft(null);
      toast({ title: "Plan saved" });
    } catch {
      /* toast already shown */
    }
  }

  async function addPlan() {
    const next = [...plans, emptyPlan()];
    try {
      await savePlans.mutateAsync(next);
      setPlans(next);
      setEditingIdx(next.length - 1);
      setDraft(next[next.length - 1]);
    } catch {
      /* toast shown */
    }
  }

  async function removePlan(idx: number) {
    const next = plans.filter((_, i) => i !== idx);
    try {
      await savePlans.mutateAsync(next);
      setPlans(next);
      if (editingIdx === idx) {
        setEditingIdx(-1);
        setDraft(null);
      }
      toast({ title: "Plan removed" });
    } catch {
      /* toast shown */
    }
  }

  function updateDraft<K extends keyof EditablePlan>(field: K, value: EditablePlan[K]) {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  function updateDraftContribution(
    tier: PlanTier,
    side: "employer" | "employee",
    value: string,
  ) {
    const num = value.trim() === "" ? null : Number(value);
    setDraft((prev) => {
      if (!prev) return prev;
      const contribs = ensureContribs(prev.contributions).map((c) =>
        c.tier === tier
          ? { ...c, [side]: Number.isFinite(num) ? num : null }
          : c,
      );
      return { ...prev, contributions: contribs };
    });
  }

  // The tier's monthly rate from the working draft (null until rates are filled).
  function tierMonthlyRate(tier: PlanTier): number | null {
    const table = draft?.rateTable;
    if (!table || table.mode !== "tier_rates") return null;
    return table.entries.find((e) => e.tier === tier)?.monthlyRate ?? null;
  }

  // Fill ONE tier's employer/employee dollars from "employer pays pct%" of that
  // tier's rate (employer = rate × %, employee = the rest). Per-tier, because an
  // employer can pay a different % on different tiers.
  function applyEmployerPctToTier(tier: PlanTier, pct: number) {
    const rate = tierMonthlyRate(tier);
    if (rate == null) return;
    const employer = Math.round(rate * (pct / 100) * 100) / 100;
    const employee = Math.round((rate - employer) * 100) / 100;
    setDraft((prev) => {
      if (!prev) return prev;
      const contribs = ensureContribs(prev.contributions).map((c) =>
        c.tier === tier ? { ...c, employer, employee } : c,
      );
      return { ...prev, contributions: contribs };
    });
  }

  const isEditing = editingIdx >= 0;

  // Aggregate per-plan validation so the Confirm button can gate on it.
  // Each plan with hasWarning blocks confirm — same predicate as the per-card
  // amber badge — so the broker can't confirm a plan with missing rates or
  // missing required fields.
  const plansWithWarnings = useMemo(() => {
    return plans
      .map((p, i) => {
        const missingCarrier = !p.carrierName?.trim();
        const missingDate = !p.effectiveStart;
        // Phase B Phase 2: per-plan readiness depends on mode + match state.
        // When broker never opened the card, p.rateMode is undefined — fall
        // back to the same heuristic the edit form uses so checks still run.
        const effMode =
          p.rateMode ??
          (isVolumeRatedPlanType(p.internalPlanType)
            ? "volume_per_thousand"
            : "tier_rates");
        let missingRates = false;
        let missingRegion = false;
        let missingContributions = false;
        if (!p.deferred && !isFsaPlanType(p.internalPlanType)) {
          if (p.rateMode === "age_tier_rates") {
            if (p.manualEntry) {
              // Manual-matrix path: require at least one cell.
              missingRates = !isRateTableComplete(p.rateTable);
            } else {
              // Library-matched path: region required, match required.
              missingRegion = !p.ratingRegion?.trim();
              missingRates = p.matchStatus !== "found";
            }
          } else if (effMode === "volume_per_thousand") {
            missingRates = !isRateTableComplete(p.rateTable);
          } else if (effMode === "tier_rates") {
            missingRates = !isRateTableComplete(p.rateTable);
            // Contributions feed `plan_contributions` rows downstream; missing
            // splits = no row for that tier = broken pricing.
            missingContributions = !isContributionsComplete(p.contributions);
          }
        }
        const reasons: string[] = [];
        if (missingCarrier) reasons.push("carrier");
        if (missingDate) reasons.push("effective date");
        if (missingRegion) reasons.push("rating region");
        if (missingRates) {
          // Tier-rate plans: be specific about which tier(s) are
          // missing instead of a generic "rate table" complaint.
          const tiers =
            effMode === "tier_rates" ? missingTierLabels(p.rateTable) : [];
          if (tiers.length > 0) {
            reasons.push(`${tiers.join(" / ")} rate`);
          } else {
            reasons.push(
              p.rateMode === "age_tier_rates"
                ? "library match or tier rates"
                : effMode === "tier_rates"
                  ? "all tier rates"
                  : "rate table",
            );
          }
        }
        // Contributions are advisory — they surface as a soft warning in the
        // panel but DO NOT contribute to the gate that disables Continue.
        const softReasons: string[] = [];
        if (missingContributions) {
          const missing = missingContributionTierLabels(p.contributions);
          softReasons.push(`contribution splits (${missing.join(", ")})`);
        }
        const hasBlocking = reasons.length > 0;
        const hasSoft = softReasons.length > 0;
        return hasBlocking || hasSoft
          ? {
              index: i,
              displayName: p.displayName || `(unnamed plan #${i + 1})`,
              reasons,
              softReasons,
              blocking: hasBlocking,
            }
          : null;
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [plans]);

  // Per-carrier summary that drives the filter-card grid above the plan
  // list. Plan count + distinct plan types are derived from the plans
  // array so the cards stay in sync as broker edits/adds/removes plans
  // mid-flow without a refetch.
  const carrierSummaries = useMemo(() => {
    return carriers.map((c) => {
      const planList = plans.filter(
        (p) => p.carrierName?.trim() === c.name.trim(),
      );
      const distinctTypes = Array.from(
        new Set(
          planList
            .map((p) => p.internalPlanType)
            .filter((t): t is string => !!t),
        ),
      );
      return {
        name: c.name,
        planCount: planList.length,
        planTypes: distinctTypes,
      };
    });
  }, [carriers, plans]);

  const visiblePlanCount = useMemo(() => {
    if (!selectedCarrierName) return plans.length;
    return plans.filter(
      (p) => p.carrierName?.trim() === selectedCarrierName.trim(),
    ).length;
  }, [plans, selectedCarrierName]);

  // Bubble in-card readiness up to the wizard so its footer can drive
  // the Confirm CTA. Mirrors the gate the old in-card button had.
  useEffect(() => {
    if (!onGateChange) return;
    let ready = true;
    let reason = "";
    if (isConfirmed) {
      // Already confirmed — footer just advances.
      ready = true;
    } else if (!canConfirm) {
      ready = false;
      reason = blockedReason ?? "Confirm the prior step first";
    } else if (plans.length === 0) {
      ready = false;
      reason = "No plans to confirm";
    } else if (isEditing) {
      ready = false;
      reason = "Save or cancel the card being edited first";
    } else if (autoMatching) {
      ready = false;
      reason = "Matching plans against library…";
    } else if (savePlans.isPending) {
      ready = false;
      reason = "Saving…";
    } else {
      const blockingCount = plansWithWarnings.filter(
        (w) => w.blocking,
      ).length;
      if (blockingCount > 0) {
        ready = false;
        reason = `${blockingCount} plan${blockingCount === 1 ? "" : "s"} need more info before continuing`;
      }
    }
    onGateChange({ ready, reason });
  }, [
    onGateChange,
    isConfirmed,
    canConfirm,
    blockedReason,
    plans.length,
    isEditing,
    autoMatching,
    savePlans.isPending,
    plansWithWarnings,
  ]);

  return (
    <div className="space-y-6">
      {/* ─── Inline state setter (client has no home state) ───
       *  The wizard's rating-region picker depends on `clientState`.
       *  Without it the region select can't filter anything and the
       *  matcher has no geographic anchor. Surfacing the setter here
       *  saves the broker a trip to the client profile page. */}
      {clientId && !clientState?.trim() && !isConfirmed && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 dark:bg-amber-900/10">
          <div className="flex flex-wrap items-center gap-3">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900">
                Set the client's home state to enable library matching
              </p>
              <p className="text-[11.5px] text-amber-800">
                Age-rated medical plans need a rating region (which depends on
                state). Pick the client's state — we'll save it back to the
                client profile.
              </p>
            </div>
            <Select
              value={pendingState}
              onValueChange={(v) => setPendingState(v)}
            >
              <SelectTrigger className="h-8 w-44 text-xs">
                <SelectValue placeholder="Select state…" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((s) => (
                  <SelectItem
                    key={s.code}
                    value={s.code}
                    className="text-xs"
                  >
                    {s.code} · {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={() => setHomeState.mutate(pendingState)}
              disabled={!pendingState || setHomeState.isPending}
            >
              {setHomeState.isPending && (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              )}
              Save state
            </Button>
          </div>
        </div>
      )}

      {autoMatching && (
        <div className="flex items-center gap-2 rounded-md border border-stone-200 bg-stone-50/60 px-3 py-2 text-[12px] text-stone-600">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-400" />
          Matching age-rated plans against the library…
        </div>
      )}

      {/* Carrier filter cards — each card filters the plan list below by
       *  carrier. Tap the same card again to clear. */}
      {carrierSummaries.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-medium">Carriers</h3>
              <span className="text-xs text-muted-foreground">
                · tap to filter plans below
              </span>
            </div>
            <div className="flex items-center gap-3">
              {selectedCarrierName && (
                <button
                  type="button"
                  onClick={() => setSelectedCarrierName(null)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear filter
                </button>
              )}
              {!isConfirmed && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCarrierDialogOpen(true)}
                  className="h-7 text-xs"
                >
                  <Plus className="mr-1 h-3 w-3" /> Add carrier
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {carrierSummaries.map((c) => {
              const isSelected = selectedCarrierName === c.name;
              const colors = carrierAvatarColor(c.name);
              return (
                <button
                  key={c.name}
                  type="button"
                  onClick={() =>
                    setSelectedCarrierName((prev) =>
                      prev === c.name ? null : c.name,
                    )
                  }
                  className={`flex flex-col gap-3 rounded-lg border bg-white p-3 text-left transition hover:shadow-sm dark:bg-stone-900 ${
                    isSelected
                      ? "border-stone-900 ring-1 ring-stone-900 dark:border-stone-100 dark:ring-stone-100"
                      : "border-stone-200 dark:border-stone-800"
                  }`}
                  aria-pressed={isSelected}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 bg-white text-sm font-semibold dark:bg-stone-900 ${colors.ring} ${colors.text}`}
                    >
                      {initialsOf(c.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">
                        {c.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {c.planCount} plan{c.planCount === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>
                  {c.planTypes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {c.planTypes.map((t) => {
                        const tile = planTypeTile(t);
                        const TileIcon = tile.Icon;
                        return (
                          <span
                            key={t}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${tile.bg} ${tile.fg}`}
                          >
                            <TileIcon className="h-3 w-3" />
                            {fmtPlanTypeLabel(t)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <CarrierDialog
        open={carrierDialogOpen}
        onOpenChange={setCarrierDialogOpen}
      />

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">
          Plans (
          {selectedCarrierName
            ? `${visiblePlanCount} of ${plans.length}`
            : plans.length}
          )
        </h3>
        {isConfirmed && !isLocked && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => reopenPlans.mutate()}
            disabled={reopenPlans.isPending}
            title="Re-open plans for editing. You'll need to re-confirm to apply changes."
          >
            {reopenPlans.isPending ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <Pencil className="mr-1 h-3 w-3" />
            )}
            Edit plans
          </Button>
        )}
        {!isConfirmed && (
          <Button
            variant="outline"
            size="sm"
            onClick={addPlan}
            disabled={isEditing || savePlans.isPending}
          >
            <Plus className="mr-1 h-3 w-3" /> Add plan
          </Button>
        )}
      </div>

      {plans.length === 0 && (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No plans yet. Click "Add plan" to create one, or upload a census file
          to auto-extract plans.
        </p>
      )}

      {(() => {
        const blocking = plansWithWarnings.filter((w) => w.blocking);
        const softOnly = plansWithWarnings.filter(
          (w) => !w.blocking && w.softReasons.length > 0,
        );
        return (
          <>
            {blocking.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900/50 dark:bg-amber-900/20">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  {isConfirmed
                    ? `${blocking.length} plan${blocking.length === 1 ? " has" : "s have"} missing data — click "Edit plans" above to fix`
                    : `Fix ${blocking.length} plan${blocking.length === 1 ? "" : "s"} before continuing`}
                </p>
                <ul className="mt-1 ml-5 list-disc text-xs text-amber-800 dark:text-amber-300">
                  {blocking.map((w) => (
                    <li key={w.index}>
                      <span className="font-medium">{w.displayName}</span> —
                      missing {w.reasons.join(", ")}
                      {w.softReasons.length > 0 && (
                        <span className="text-amber-700/80">
                          {" "}
                          (also: {w.softReasons.join(", ")})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {softOnly.length > 0 && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-900/50 dark:bg-blue-900/20">
                <p className="font-medium text-blue-800 dark:text-blue-200">
                  {softOnly.length} plan
                  {softOnly.length === 1 ? " has" : "s have"} optional fields
                  missing (won't block continuing)
                </p>
                <ul className="mt-1 ml-5 list-disc text-xs text-blue-800 dark:text-blue-300">
                  {softOnly.map((w) => (
                    <li key={w.index}>
                      <span className="font-medium">{w.displayName}</span> —{" "}
                      {w.softReasons.join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        );
      })()}

      <div className="space-y-4">
        {plans.map((p, i) => {
          // Carrier filter from the card grid above. Plans that don't match
          // the selected carrier render nothing — keeping the outer `i` as
          // the global plans-array index so editingIdx / setPlans(next) /
          // plansWithWarnings keying all stay correct.
          if (
            selectedCarrierName &&
            p.carrierName?.trim() !== selectedCarrierName.trim()
          ) {
            return null;
          }
          const editing = editingIdx === i;
          const workingCopy = editing ? draft! : p;
          const missingCarrier = !workingCopy.carrierName?.trim();
          const missingDate = !workingCopy.effectiveStart;
          // Same effective-mode fallback as `plansWithWarnings`: when broker
          // never set rateMode explicitly, the plan still has an inferred
          // shape based on its type.
          const cardEffMode =
            workingCopy.rateMode ??
            (isVolumeRatedPlanType(workingCopy.internalPlanType)
              ? "volume_per_thousand"
              : "tier_rates");
          let missingRates = false;
          let missingRegion = false;
          let missingContributions = false;
          if (
            !workingCopy.deferred &&
            !isFsaPlanType(workingCopy.internalPlanType)
          ) {
            if (workingCopy.rateMode === "age_tier_rates") {
              if (workingCopy.manualEntry) {
                missingRates = !isRateTableComplete(workingCopy.rateTable);
              } else {
                missingRegion = !workingCopy.ratingRegion?.trim();
                missingRates = workingCopy.matchStatus !== "found";
              }
            } else if (cardEffMode === "volume_per_thousand") {
              missingRates = !isRateTableComplete(workingCopy.rateTable);
            } else if (cardEffMode === "tier_rates") {
              missingRates = !isRateTableComplete(workingCopy.rateTable);
              missingContributions = !isContributionsComplete(
                workingCopy.contributions,
              );
            }
          }
          // Amber border surfaces *blocking* missing data — broker must fix
          // these before confirm. Soft warnings (e.g. contributions) get a
          // softer treatment via the slab/pill below, no amber chrome.
          const hasWarning =
            missingCarrier || missingDate || missingRegion || missingRates;

          return (
            <Card
              key={i}
              className={
                hasWarning
                  ? "border-amber-300 bg-amber-50/40 dark:bg-amber-900/5"
                  : undefined
              }
            >
              <CardContent className="p-0">
                {(() => {
                  // Per-card derived rate preview (mirrors confirmPlans aggregation).
                  const isVolumeRated = isVolumeRatedPlanType(
                    workingCopy.internalPlanType,
                  );
                  // Effective rate shape — prefers broker's chosen mode over
                  // the plan-type heuristic, so a medical/dental plan
                  // switched to "Per $1,000" is treated as volume.
                  const effectiveRateMode: "tier_rates" | "age_tier_rates" | "volume_per_thousand" =
                    workingCopy.rateMode ??
                    (isVolumeRated ? "volume_per_thousand" : "tier_rates");
                  const previewByTier = ratePreviewByPlanKey.get(
                    previewKey(workingCopy),
                  );
                  const ageRatedTiers = previewByTier
                    ? Array.from(previewByTier.entries()).filter(
                        ([, agg]) => agg.distinctRates.size > 2,
                      )
                    : [];
                  // FSA plans have no plan-level rates — every employee elects
                  // their own annual contribution — so the "rates vary" warning
                  // is nonsense for them. Suppress it.
                  const isFsa = isFsaPlanType(workingCopy.internalPlanType);
                  const isLikelyAgeRated =
                    !isFsa &&
                    effectiveRateMode === "tier_rates" &&
                    ageRatedTiers.length > 0;

                  // Header tile (icon + tint).
                  const tile = planTypeTile(workingCopy.internalPlanType);
                  const TileIcon = tile.Icon;
                  const planName =
                    workingCopy.displayName?.trim() ||
                    fmtPlanTypeLabel(workingCopy.internalPlanType);

                  // Pick the most actionable warning pill. Order matters:
                  // rates win over contributions (broker can't split a
                  // premium they haven't entered yet); age-rated review is
                  // the lowest priority since it's a recommendation, not a
                  // blocker.
                  const warningPill = missingCarrier || missingDate
                    ? { label: "Plan details missing", tone: "amber" as const }
                    : missingRates
                      ? { label: "Rates required", tone: "blue" as const }
                      : missingContributions
                        ? { label: "Contributions missing (optional)", tone: "blue" as const }
                        : isLikelyAgeRated
                          ? { label: "Rates likely age-rated — review", tone: "amber" as const }
                          : null;

                  // Inline warning slab body — terse, broker-fluent.
                  const tierLabelsMissing =
                    cardEffMode === "tier_rates"
                      ? missingTierLabels(workingCopy.rateTable)
                      : [];
                  const slab = missingCarrier || missingDate
                    ? {
                        body: "Carrier, policy, or effective dates didn't come through in the source file.",
                        action: "Fill them in, plus the rate table.",
                      }
                    : missingRates
                      ? cardEffMode === "volume_per_thousand"
                        ? {
                            body: "No rate imported.",
                            action: "Add the per-benefit-amount or per-age-band rate table.",
                          }
                        : cardEffMode === "tier_rates"
                          ? {
                              body:
                                tierLabelsMissing.length > 0
                                  ? `Missing rate for ${tierLabelsMissing.join(", ")}.`
                                  : "No tier rates filled.",
                              action: "Fill all 4 tiers in the rates section.",
                            }
                          : {
                              body: "No library match.",
                              action: "Pick a match or switch to a manual age × tier matrix.",
                            }
                      : missingContributions
                        ? {
                            body: "Employer/employee splits not entered (optional — won't block confirm).",
                            action:
                              'Open "Monthly contributions by tier" to add them now, or set them later in the plan-details page.',
                          }
                        : isLikelyAgeRated
                          ? {
                              body: "Rates vary across employees — likely age-rated.",
                              action: "Switch to Age rated and confirm the library match.",
                            }
                          : null;

                  return (
                    <>
                {/* ─── Header (icon + identity + actions) ─── */}
                <div className="flex items-start gap-4 p-5">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${tile.bg}`}
                  >
                    <TileIcon className={`h-[18px] w-[18px] ${tile.fg}`} />
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold leading-tight">
                        {planName}
                      </span>
                      {workingCopy.easePlanType && (
                        <Badge
                          variant="outline"
                          className="font-mono text-[10px] font-normal"
                        >
                          source: {workingCopy.easePlanType}
                        </Badge>
                      )}
                      {warningPill && !editing && (
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            warningPill.tone === "blue"
                              ? "border-blue-300 bg-blue-50 text-blue-700"
                              : "border-amber-300 bg-amber-50 text-amber-700"
                          }`}
                        >
                          <AlertTriangle className="mr-1 h-2.5 w-2.5" />
                          {warningPill.label}
                        </Badge>
                      )}
                    </div>
                    {!editing && (
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
                        <span>
                          Carrier ·{" "}
                          <span className="font-medium text-foreground">
                            {p.carrierName?.trim() || "—"}
                          </span>
                        </span>
                        <span>
                          Effective ·{" "}
                          <span className="font-mono text-foreground">
                            {p.effectiveStart
                              ? `${fmtDate(p.effectiveStart)} → ${fmtDate(p.effectiveEnd)}`
                              : "—"}
                          </span>
                        </span>
                        <span>
                          Policy ·{" "}
                          <span className="font-mono text-foreground">
                            {p.policyNumber || "—"}
                          </span>
                        </span>
                        {p.waitingPeriodAmount != null && (
                          <span>
                            Waiting ·{" "}
                            <span className="font-medium text-foreground">
                              {fmtWaiting(p)}
                            </span>
                          </span>
                        )}
                        {effectiveRateMode === "age_tier_rates" && (
                          <span>
                            Region ·{" "}
                            {p.ratingRegion?.trim() ? (
                              <span className="font-medium text-foreground">
                                {p.ratingRegion}
                              </span>
                            ) : (
                              <span className="font-medium text-amber-700">
                                — required
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {!isConfirmed && !editing && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(i)}
                        disabled={isEditing}
                        className="h-7 px-2 text-xs"
                      >
                        <Pencil className="mr-1 h-3 w-3" /> Edit
                      </Button>
                      <button
                        type="button"
                        onClick={() => removePlan(i)}
                        className="text-muted-foreground/60 hover:text-red-500"
                        disabled={isEditing || savePlans.isPending}
                        title="Remove plan"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  {editing && (
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelEdit}
                        disabled={savePlans.isPending}
                      >
                        <X className="mr-1 h-3 w-3" /> Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveEdit}
                        disabled={savePlans.isPending}
                      >
                        {savePlans.isPending && (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        )}
                        Save
                      </Button>
                    </div>
                  )}
                </div>

                {/* ─── RATES strip (read-only mode) ─── */}
                {!editing && (
                  <div className="flex items-center justify-between gap-4 border-t bg-stone-50/40 px-5 py-3 dark:bg-stone-900/20">
                    <div className="flex flex-1 flex-wrap items-baseline gap-x-6 gap-y-1.5 min-w-0">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      RATES
                    </span>
                    {(() => {
                      // FSA-family (Health FSA, Dependent Care FSA,
                      // Transit/Commuter): no plan-level rates. Per-
                      // employee elected amounts ride through
                      // enrollments.coverageAmount at confirm time.
                      if (isFsaPlanType(workingCopy.internalPlanType)) {
                        return (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-2 py-0.5 text-[11.5px] text-stone-700">
                            Employee contributions ·{" "}
                            <span className="font-medium text-stone-900">
                              elections carry forward
                            </span>
                          </span>
                        );
                      }
                      // Volume mode (broker-chosen OR auto-derived from a
                      // volume-rated plan type). Composite mode shows the
                      // single rate; tiered mode summarises the banded rows
                      // (tiered has no composite entry — only ageBand rows —
                      // so the composite lookup alone would always read
                      // "not configured" for tiered).
                      if (effectiveRateMode === "volume_per_thousand") {
                        const tbl = workingCopy.rateTable;
                        if (tbl?.mode === "volume_per_thousand") {
                          if (tbl.rateType === "tiered") {
                            const banded = tbl.entries.filter(
                              (e) => e.ageBand && e.ratePerUnit != null,
                            );
                            if (banded.length > 0) {
                              return (
                                <span className="text-xs">
                                  Per benefit amount ·{" "}
                                  <strong className="font-semibold">
                                    {banded.length} age band
                                    {banded.length === 1 ? "" : "s"}
                                  </strong>
                                </span>
                              );
                            }
                          } else {
                            const v = tbl.entries.find(
                              (e) =>
                                (e.ageBand == null || e.ageBand === "") &&
                                e.ratePerUnit != null,
                            )?.ratePerUnit;
                            if (v != null) {
                              return (
                                <span className="text-xs">
                                  Per benefit amount{" "}
                                  <strong className="font-semibold">
                                    {fmtUsdShort(Number(v))}
                                  </strong>
                                </span>
                              );
                            }
                          }
                        }
                        return (
                          <span className="text-xs italic text-amber-700">
                            not configured
                          </span>
                        );
                      }

                      // Age-tier mode (Phase B Phase 2): show library-match
                      // status. Rates land at confirm time from the library
                      // — they don't live on the plan card.
                      if (effectiveRateMode === "age_tier_rates") {
                        // Manual age × tier matrix path. Show how complete
                        // the table is and an Edit affordance.
                        if (workingCopy.manualEntry) {
                          const tbl = workingCopy.rateTable;
                          const filled =
                            tbl?.mode === "age_tier_rates"
                              ? tbl.entries.filter(
                                  (e) =>
                                    e.monthlyRate != null && e.monthlyRate >= 0,
                                ).length
                              : 0;
                          return (
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-2 py-0.5 text-[11.5px] text-stone-700">
                                Manual rates ·{" "}
                                <strong className="font-semibold text-stone-900">
                                  {filled} cells filled
                                </strong>
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  applyPlanMatchUpdate(i, {
                                    manualEntry: false,
                                    rateTable: undefined,
                                    matchStatus: undefined,
                                    matchedWbId: null,
                                    matchedWbName: null,
                                    candidates: [],
                                    libraryRates: null,
                                  })
                                }
                                className="text-[11px] font-medium text-stone-500 underline-offset-2 hover:text-stone-900 hover:underline"
                              >
                                Search library instead
                              </button>
                            </span>
                          );
                        }
                        // A confirmed match always wins — even if region is
                        // missing, the broker should see the pill (and the
                        // "Wrong match?" affordance to challenge it). The
                        // region warning only kicks in when there's no
                        // match yet AND nothing else useful to show.
                        if (workingCopy.matchStatus === "found") {
                          return (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11.5px] text-emerald-900">
                              <Check className="h-3 w-3 shrink-0 text-emerald-700" />
                              <span>
                                Library match ·{" "}
                                <strong className="font-semibold text-stone-900">
                                  {workingCopy.matchedWbName ?? "—"}
                                </strong>
                              </span>
                            </span>
                          );
                        }
                        // Reusable inline escape-hatch buttons for any
                        // "not matched yet" state. Lets the broker bail out
                        // of the library flow without entering Edit mode.
                        // "Enter rates manually" keeps the plan in age-rated
                        // mode but flips manualEntry on, so the edit form
                        // renders the age × tier matrix editor instead of
                        // MatchPanel. Initializes the matrix with the
                        // default age bands × 4 tiers so the broker has a
                        // grid to fill in, not a blank slate.
                        const switchToManualBtn = (
                          <button
                            type="button"
                            onClick={() => {
                              void applyPlanMatchUpdate(i, {
                                rateMode: "age_tier_rates",
                                rateTable: emptyRateTable("age_tier_rates"),
                                manualEntry: true,
                                matchStatus: undefined,
                                matchedWbId: null,
                                matchedWbName: null,
                                candidates: [],
                                libraryRates: null,
                                deferred: false,
                              });
                              startEdit(i);
                            }}
                            className="rounded-md border border-stone-300 bg-white px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:bg-stone-100"
                          >
                            Enter rates manually
                          </button>
                        );
                        const skipBtn = (
                          <button
                            type="button"
                            onClick={() =>
                              applyPlanMatchUpdate(i, {
                                deferred: true,
                                // Fully unlink any library match so a
                                // confirmed-but-skipped plan doesn't pull
                                // rates at migration confirm time.
                                matchStatus: undefined,
                                matchedWbId: null,
                                matchedWbName: null,
                                candidates: [],
                                libraryRates: null,
                              })
                            }
                            className="rounded-md px-2 py-0.5 text-[11px] font-medium text-stone-500 hover:bg-stone-100 hover:text-stone-700"
                          >
                            Skip · add later
                          </button>
                        );
                        if (workingCopy.matchStatus === "no_library_data") {
                          return (
                            <span className="inline-flex flex-wrap items-center gap-2">
                              <span className="text-xs italic text-stone-500">
                                Library not yet available for{" "}
                                {clientState ?? "this state"} —
                              </span>
                              {switchToManualBtn}
                            </span>
                          );
                        }
                        if (workingCopy.deferred) {
                          return (
                            <span className="inline-flex flex-wrap items-center gap-2">
                              <span className="text-xs italic text-stone-500">
                                Deferred · add rates later —
                              </span>
                              {switchToManualBtn}
                            </span>
                          );
                        }
                        // No match yet — when region is the missing piece,
                        // surface that specifically. Otherwise this carrier
                        // genuinely isn't in (or matchable against) the
                        // library; offer the manual-entry / skip escape.
                        if (
                          !workingCopy.ratingRegion?.trim() &&
                          hasRatingAreas
                        ) {
                          return (
                            <span className="text-xs italic text-amber-700">
                              Pick a rating region to find a library match
                            </span>
                          );
                        }
                        return (
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <span className="text-xs italic text-amber-700">
                              Not in library —
                            </span>
                            {switchToManualBtn}
                            {skipBtn}
                          </span>
                        );
                      }

                      // Tier rates: prefer broker-entered; fall back to
                      // auto-derived preview. Render ALL four tiers —
                      // missing ones surface as amber "missing" chips
                      // so brokers notice partial fills without
                      // entering edit mode.
                      const brokerEntries =
                        workingCopy.rateTable?.mode === "tier_rates"
                          ? workingCopy.rateTable.entries
                          : null;
                      type ChipState =
                        | { kind: "filled"; label: string; value: number; note?: string }
                        | { kind: "missing"; label: string };
                      const tierChips: ChipState[] = [];
                      for (const t of TIERS) {
                        const broker = brokerEntries?.find(
                          (e) => e.tier === t.key,
                        );
                        const auto = previewByTier?.get(t.key);
                        if (broker?.monthlyRate != null) {
                          tierChips.push({
                            kind: "filled",
                            label: t.label,
                            value: broker.monthlyRate,
                          });
                        } else if (auto && auto.monthlyRate > 0) {
                          tierChips.push({
                            kind: "filled",
                            label: t.label,
                            value: auto.monthlyRate,
                            note:
                              auto.distinctRates.size > 2
                                ? `placeholder · ${auto.distinctRates.size} rates seen`
                                : undefined,
                          });
                        } else {
                          tierChips.push({ kind: "missing", label: t.label });
                        }
                      }
                      const filledCount = tierChips.filter(
                        (c) => c.kind === "filled",
                      ).length;
                      if (filledCount === 0) {
                        return (
                          <span className="text-xs italic text-muted-foreground">
                            no rates yet
                          </span>
                        );
                      }
                      return tierChips.map((c) =>
                        c.kind === "filled" ? (
                          <span key={c.label} className="text-xs">
                            {c.label}{" "}
                            <strong className="font-semibold">
                              {fmtUsdShort(c.value)}
                            </strong>
                            {c.note && (
                              <span className="ml-1 text-[11px] italic text-amber-700">
                                · {c.note}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span
                            key={c.label}
                            className="inline-flex items-center gap-1 text-xs"
                          >
                            <AlertTriangle className="h-3 w-3 text-amber-600" />
                            <span className="text-amber-700">
                              {c.label}{" "}
                              <em className="italic">missing</em>
                            </span>
                          </span>
                        ),
                      );
                    })()}
                    </div>
                    {effectiveRateMode === "age_tier_rates" &&
                      workingCopy.matchStatus === "found" && (
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setOpenBrowseIdx((prev) =>
                                prev === i ? null : i,
                              )
                            }
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] font-medium text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                          >
                            {openBrowseIdx === i ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                            {openBrowseIdx === i
                              ? "Hide alternates"
                              : "Browse alternates"}
                          </button>
                          {Array.isArray(workingCopy.libraryRates) &&
                            workingCopy.libraryRates.length > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenRatesIdx((prev) =>
                                    prev === i ? null : i,
                                  )
                                }
                                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] font-medium text-emerald-800 hover:bg-emerald-100/60 hover:text-emerald-900"
                              >
                                {openRatesIdx === i ? (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                )}
                                {openRatesIdx === i
                                  ? "Hide rate table"
                                  : "Show rate table"}
                              </button>
                            )}
                        </div>
                      )}
                  </div>
                )}

                {/* ─── Inline rate-table preview (read-only disclosure) ─── */}
                {!editing &&
                  openRatesIdx === i &&
                  workingCopy.matchStatus === "found" &&
                  Array.isArray(workingCopy.libraryRates) &&
                  workingCopy.libraryRates.length > 0 && (
                    <div className="border-t bg-white px-5 py-3 dark:bg-stone-900/10">
                      <MatchRateTablePreview
                        rates={workingCopy.libraryRates}
                      />
                    </div>
                  )}

                {/* ─── Inline browse-alternates panel (read-only) ─── */}
                {!editing &&
                  openBrowseIdx === i &&
                  workingCopy.matchStatus === "found" && (
                    <div className="space-y-3 border-t bg-white px-5 py-3 dark:bg-stone-900/10">
                      <BrowseAlternatesList
                        carrierName={workingCopy.carrierName ?? ""}
                        matchedWbId={workingCopy.matchedWbId ?? null}
                        listCarrierLibrary={() => listCarrierLibrary(i)}
                        onPickCandidate={(cand) =>
                          applyPlanMatchUpdate(i, {
                            matchedWbId: cand.wbId,
                            matchedWbName: cand.name,
                            libraryRates: null,
                          }).then(() => {
                            void runPopoverMatch(i, cand.name);
                            setOpenBrowseIdx(null);
                          })
                        }
                      />
                      <div className="flex items-center justify-end gap-1 border-t border-stone-100 pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11.5px] text-stone-600 hover:text-stone-900"
                          onClick={() => {
                            void applyPlanMatchUpdate(i, {
                              matchStatus: undefined,
                              matchedWbId: null,
                              matchedWbName: null,
                              candidates: [],
                              libraryRates: null,
                            });
                            setOpenBrowseIdx(null);
                          }}
                        >
                          Unlink
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11.5px] text-stone-600 hover:text-stone-900"
                          onClick={() => {
                            void applyPlanMatchUpdate(i, {
                              deferred: true,
                              matchStatus: undefined,
                              matchedWbId: null,
                              matchedWbName: null,
                              candidates: [],
                              libraryRates: null,
                            });
                            setOpenBrowseIdx(null);
                          }}
                        >
                          Skip · add later
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[11.5px]"
                          onClick={() => {
                            void applyPlanMatchUpdate(i, {
                              rateMode: "age_tier_rates",
                              rateTable: emptyRateTable("age_tier_rates"),
                              manualEntry: true,
                              matchStatus: undefined,
                              matchedWbId: null,
                              matchedWbName: null,
                              candidates: [],
                              libraryRates: null,
                              deferred: false,
                            });
                            setOpenBrowseIdx(null);
                            startEdit(i);
                          }}
                        >
                          Enter rates manually
                        </Button>
                      </div>
                    </div>
                  )}

                {/* ─── Inline warning slab (read-only mode, when there's an issue) ─── */}
                {!editing && slab && (
                  <div className="flex items-start gap-3 border-t border-amber-300 bg-amber-50 px-5 py-3.5 text-xs text-amber-900 dark:bg-amber-900/10 dark:text-amber-100">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                    <p className="min-w-0 flex-1">
                      <strong>{slab.body}</strong> {slab.action}
                    </p>
                    <Button
                      size="sm"
                      onClick={() => startEdit(i)}
                      disabled={isEditing}
                      className="shrink-0 bg-amber-600 text-white hover:bg-amber-700"
                    >
                      Fix this
                    </Button>
                  </div>
                )}

                {/* ─── Edit view ─── */}
                {editing && draft && (() => {
                  // Rating-region picker rendered as a shared block so the
                  // top-of-form (age-rated, required) and the Advanced
                  // disclosure (non-age, optional) don't have to duplicate
                  // 70+ lines of JSX. `required` toggles asterisk + amber
                  // highlight when the field is empty.
                  const renderRatingRegionPicker = (required: boolean) => {
                    const isMissing =
                      required && !draft.ratingRegion?.trim();
                    const labelEl = (
                      <Label className="text-xs">
                        Rating region
                        {required && (
                          <span className="text-red-500"> *</span>
                        )}
                      </Label>
                    );
                    // Region change re-fires the matcher via the debounced
                    // auto-refetch effect (watches draft.ratingRegion).
                    const setRegion = (value: string | null) => {
                      updateDraft("ratingRegion", value);
                    };
                    if (!hasRatingAreas) {
                      // State has no seeded rating areas. Don't render an
                      // editable field — rating regions are structured
                      // reference data, free text would just produce
                      // garbage that can't link to anything downstream.
                      // The plan is auto-deferred by the auto-match flow.
                      return (
                        <div className="space-y-1">
                          {labelEl}
                          <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-[11.5px] text-stone-600">
                            Rating regions for{" "}
                            <strong className="text-stone-800">
                              {clientState ?? "this state"}
                            </strong>{" "}
                            aren't configured yet. Age-rated plans will be
                            deferred — rates can be added manually after
                            migration.
                          </div>
                        </div>
                      );
                    }
                    const NONE = "__none__";
                    const LEGACY = "__legacy__";
                    const saved = draft.ratingRegion?.trim() ?? "";
                    const isSeeded = saved && ratingAreaLabels.includes(saved);
                    const hasLegacy = !!saved && !isSeeded;
                    const selectValue = saved
                      ? isSeeded
                        ? saved
                        : LEGACY
                      : NONE;
                    return (
                      <div className="space-y-1">
                        {labelEl}
                        <Select
                          value={selectValue}
                          onValueChange={(v) => {
                            if (v === NONE) setRegion(null);
                            else if (v === LEGACY) {
                              // No-op — the legacy item is disabled but
                              // shadcn still routes the value through here
                              // on initial render. Don't clear or change.
                            } else {
                              setRegion(v);
                            }
                          }}
                        >
                          <SelectTrigger
                            className={`h-8 text-sm ${isMissing ? "border-amber-400 bg-amber-50" : ""}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {hasLegacy && (
                              <SelectItem
                                value={LEGACY}
                                disabled
                                className="text-stone-500"
                              >
                                {saved} (legacy · pick a current region)
                              </SelectItem>
                            )}
                            <SelectItem value={NONE}>(none)</SelectItem>
                            {ratingAreaRows.map((r) => (
                              <SelectItem key={r.id} value={r.label}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-muted-foreground">
                          {`Geographic rating area for ${clientState} — required for age-rated medical plans (ACA prices by region).`}
                        </p>
                      </div>
                    );
                  };
                  const ageRatedMode = draft.rateMode === "age_tier_rates";
                  return (
                  <div className="space-y-4 border-t bg-stone-50/30 p-4 dark:bg-stone-900/10">
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Plan type</Label>
                        <Select
                          value={draft.internalPlanType ?? ""}
                          onValueChange={(v) => updateDraft("internalPlanType", v)}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue placeholder="Pick a type" />
                          </SelectTrigger>
                          <SelectContent>
                            {PLAN_TYPE_OPTIONS.map((opt) => (
                              <SelectItem
                                key={opt.value}
                                value={opt.value}
                                className="text-xs"
                              >
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">
                          Carrier <span className="text-red-500">*</span>
                        </Label>
                        <CarrierPicker
                          carrierName={draft.carrierName}
                          carrierId={draft.carrierId ?? null}
                          required={missingCarrier}
                          onChange={({ id, name }) => {
                            // Update both fields in one go so they stay
                            // in sync. The picker calls this for every
                            // change — free-text typing, suggestion
                            // confirm, browse pick, and create-new.
                            setDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    carrierName: name,
                                    carrierId: id,
                                  }
                                : prev,
                            );
                          }}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Display name</Label>
                        <Input
                          className="h-8 text-sm"
                          value={draft.displayName}
                          onChange={(e) =>
                            updateDraft("displayName", e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">
                          Effective date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="date"
                          className={`h-8 text-sm ${missingDate ? "border-amber-400 bg-amber-50" : ""}`}
                          value={draft.effectiveStart ?? ""}
                          onChange={(e) =>
                            updateDraft(
                              "effectiveStart",
                              e.target.value || null,
                            )
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Termination date</Label>
                        <Input
                          type="date"
                          className="h-8 text-sm"
                          value={draft.effectiveEnd ?? ""}
                          onChange={(e) =>
                            updateDraft("effectiveEnd", e.target.value || null)
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Policy / Group #</Label>
                        <Input
                          className="h-8 text-sm"
                          value={draft.policyNumber}
                          onChange={(e) =>
                            updateDraft("policyNumber", e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Waiting period</Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            className="h-8 w-20 text-sm"
                            value={draft.waitingPeriodAmount ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateDraft(
                                "waitingPeriodAmount",
                                v.trim() === "" ? null : Number(v),
                              );
                            }}
                            placeholder="0"
                          />
                          <Select
                            value={
                              draft.waitingPeriodUnit ?? WaitingPeriodType.DAYS
                            }
                            onValueChange={(v) =>
                              updateDraft(
                                "waitingPeriodUnit",
                                v as WaitingPeriodType,
                              )
                            }
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {WAITING_PERIOD_TYPE_OPTIONS.map((opt) => (
                                <SelectItem
                                  key={opt.value}
                                  value={opt.value}
                                  className="text-xs"
                                >
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {ageRatedMode && renderRatingRegionPicker(true)}
                    </div>

                    <details>
                      <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground">
                        {ageRatedMode
                          ? "Advanced (subgroup)"
                          : "Advanced (subgroup, rating region)"}
                      </summary>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Subgroup #</Label>
                          <Input
                            className="h-8 text-sm"
                            value={draft.subgroupNumber}
                            onChange={(e) =>
                              updateDraft("subgroupNumber", e.target.value)
                            }
                            placeholder="optional"
                          />
                          <p className="text-[10px] text-muted-foreground">
                            Carrier-assigned sub-policy id (e.g. Unum
                            497031/497032). From Ease if present.
                          </p>
                        </div>
                        {!ageRatedMode && (
                          <>
                        <div className="space-y-1">
                          <Label className="text-xs">Rating region</Label>
                          {hasRatingAreas ? (
                            (() => {
                              // The saved value may be from outside the
                              // seeded list (carrier-specific zone). Surface
                              // it as a "Custom" entry so the broker sees
                              // their value but can switch to a seeded one.
                              const NONE = "__none__";
                              const CUSTOM = "__custom__";
                              const saved = draft.ratingRegion?.trim() ?? "";
                              const isSeeded =
                                saved && ratingAreaLabels.includes(saved);
                              const selectValue = saved
                                ? isSeeded
                                  ? saved
                                  : CUSTOM
                                : NONE;
                              const showCustomInput =
                                selectValue === CUSTOM;
                              return (
                                <>
                                  <Select
                                    value={selectValue}
                                    onValueChange={(v) => {
                                      if (v === NONE)
                                        updateDraft("ratingRegion", null);
                                      else if (v === CUSTOM) {
                                        // Keep current free-text value; if
                                        // none, seed empty so the input is
                                        // editable.
                                        if (!saved)
                                          updateDraft("ratingRegion", "");
                                      } else {
                                        updateDraft("ratingRegion", v);
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={NONE}>
                                        (none)
                                      </SelectItem>
                                      {ratingAreaRows.map((r) => (
                                        <SelectItem
                                          key={r.id}
                                          value={r.label}
                                        >
                                          {r.label}
                                        </SelectItem>
                                      ))}
                                      <SelectItem value={CUSTOM}>
                                        Custom…
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {showCustomInput && (
                                    <Input
                                      className="h-8 text-sm"
                                      value={saved}
                                      onChange={(e) =>
                                        updateDraft(
                                          "ratingRegion",
                                          e.target.value || null,
                                        )
                                      }
                                      placeholder="e.g. Region 5"
                                    />
                                  )}
                                </>
                              );
                            })()
                          ) : (
                            <Input
                              className="h-8 text-sm"
                              value={draft.ratingRegion ?? ""}
                              onChange={(e) =>
                                updateDraft(
                                  "ratingRegion",
                                  e.target.value || null,
                                )
                              }
                              placeholder="e.g. CA, Region 5"
                            />
                          )}
                          <p className="text-[10px] text-muted-foreground">
                            {hasRatingAreas
                              ? `Geographic rating area for ${clientState} (health only — premiums vary by region).`
                              : "Geographic rating area (health only — premiums vary by region)."}
                          </p>
                        </div>
                          </>
                        )}
                      </div>
                    </details>

                    {/* ─── Rate model + body (always visible in edit) ─── */}
                    {isFsaPlanType(draft.internalPlanType) ? (
                      <div className="rounded-md border border-stone-200 bg-stone-50 px-4 py-3 text-[12.5px] leading-relaxed text-stone-700">
                        Each employee's elected contribution is captured per-enrollment in Step 4 and
                        carries forward to deductions automatically.
                      </div>
                    ) : (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <Label className="text-[11px] uppercase tracking-[0.05em] text-stone-500 font-medium">
                          Rate model
                        </Label>
                        <RateModelToggle
                          value={draft.rateMode ?? "tier_rates"}
                          onChange={(newMode) => {
                            setDraft((prev) => {
                              if (!prev) return prev;
                              if (prev.rateMode === newMode) return prev;
                              const next: EditablePlan = {
                                ...prev,
                                rateMode: newMode,
                                // Reset the local rate table when leaving age
                                // mode (or keep tier table if still tier).
                                rateTable:
                                  newMode === "age_tier_rates"
                                    ? undefined
                                    : emptyRateTable(newMode, prev.internalPlanType),
                                // Clear library match when leaving age mode.
                                matchStatus:
                                  newMode === "age_tier_rates"
                                    ? "searching"
                                    : undefined,
                                matchedWbId:
                                  newMode === "age_tier_rates" ? null : undefined,
                                matchedWbName:
                                  newMode === "age_tier_rates" ? null : undefined,
                                candidates:
                                  newMode === "age_tier_rates" ? [] : undefined,
                                libraryRates: undefined,
                                // "Switch to tier rates" un-defers.
                                deferred: false,
                              };
                              return next;
                            });
                            // Auto-fire the matcher when entering age mode.
                            if (newMode === "age_tier_rates") {
                              if (!hasRatingAreas) {
                                setDraft((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        matchStatus: "no_library_data",
                                        deferred: true,
                                      }
                                    : prev,
                                );
                              } else {
                                queueMicrotask(() => fireMatch());
                              }
                            }
                          }}
                        />
                        {draft.deferred && (
                          <Badge
                            variant="outline"
                            className="text-[10px] border-stone-300 bg-stone-50 text-stone-600"
                          >
                            Deferred · add rates later
                          </Badge>
                        )}
                      </div>

                      {draft.rateMode === "age_tier_rates" &&
                      !draft.manualEntry &&
                      draft.deferred ? (
                        // Plan is explicitly deferred. Don't render
                        // MatchPanel — it'd fall through to its
                        // "Looking up in library…" default and confuse
                        // the broker, who just told us to skip.
                        <div className="flex items-start gap-2 rounded-[8px] border border-stone-200 bg-stone-50 px-4 py-3 text-stone-700">
                          <span className="text-[12.5px] leading-relaxed">
                            Skipped. No rates will land at confirm time —
                            you can add them in the plan editor after
                            migration.{" "}
                            <button
                              type="button"
                              onClick={() => {
                                setDraft((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        deferred: false,
                                        matchStatus: "searching",
                                      }
                                    : prev,
                                );
                                queueMicrotask(() => fireMatch());
                              }}
                              className="font-medium text-stone-900 underline-offset-2 hover:underline"
                            >
                              Undo skip
                            </button>
                          </span>
                        </div>
                      ) : draft.rateMode === "age_tier_rates" &&
                        !draft.manualEntry ? (
                        <MatchPanel
                          status={draft.matchStatus}
                          carrierName={draft.carrierName ?? ""}
                          matchedWbId={draft.matchedWbId ?? null}
                          matchedWbName={draft.matchedWbName}
                          candidates={draft.candidates}
                          stateLabel={clientState ?? null}
                          rates={draft.libraryRates ?? null}
                          ratesRatingAreaId={draft.ratesRatingAreaId ?? null}
                          ratesEffectiveMonth={draft.ratesEffectiveMonth ?? null}
                          ratesSource={draft.ratesSource ?? null}
                          ratesReason={draft.ratesReason ?? null}
                          isRefining={matchLibrary.isPending}
                          listCarrierLibrary={() =>
                            listCarrierLibrary(editingIdx)
                          }
                          onRefine={(q) => fireMatch({ query: q })}
                          onPickCandidate={(cand) => {
                            // Optimistically swap the match identity, then
                            // re-fire the matcher with the candidate's exact
                            // name so we pick up the right rates jsonb. Without
                            // this, libraryRates stays null and the Show rate
                            // table button is correctly hidden — but the
                            // broker just sees the rates section empty out
                            // and wonders where the button went.
                            setDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    matchStatus: "searching",
                                    matchedWbId: cand.wbId,
                                    matchedWbName: cand.name,
                                    libraryRates: null,
                                    deferred: false,
                                  }
                                : prev,
                            );
                            queueMicrotask(() =>
                              fireMatch({ query: cand.name }),
                            );
                          }}
                          onSwitchToManual={() =>
                            // Flip into manual age × tier matrix mode.
                            // Keep age_tier_rates as the rateMode so the
                            // wizard still treats this as age-rated; the
                            // edit form renders RateTableEditor instead
                            // of MatchPanel when manualEntry is true.
                            setDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    rateMode: "age_tier_rates",
                                    rateTable:
                                      emptyRateTable("age_tier_rates"),
                                    manualEntry: true,
                                    matchStatus: undefined,
                                    matchedWbId: undefined,
                                    matchedWbName: undefined,
                                    candidates: undefined,
                                    libraryRates: undefined,
                                    deferred: false,
                                  }
                                : prev,
                            )
                          }
                          onDefer={() =>
                            // Defer = "skip the library, add rates later".
                            // Clear the match identity so a stale found
                            // state doesn't pull rates at confirm time.
                            setDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    deferred: true,
                                    matchStatus: undefined,
                                    matchedWbId: null,
                                    matchedWbName: null,
                                    candidates: [],
                                    libraryRates: null,
                                  }
                                : prev,
                            )
                          }
                          onUnlink={() => {
                            // Clear the match and hand control back to the
                            // broker. We *keep* `candidates` so they're
                            // still visible in the NotFoundPanel that takes
                            // over — running the same name through the
                            // matcher again would just bounce right back to
                            // the auto-pick they didn't want.
                            setDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    matchStatus: "not_found",
                                    matchedWbId: null,
                                    matchedWbName: null,
                                    libraryRates: null,
                                  }
                                : prev,
                            );
                          }}
                        />
                      ) : (
                        <div className="space-y-2">
                          {draft.rateMode === "age_tier_rates" &&
                            draft.manualEntry && (
                              <div className="flex items-center justify-between rounded-md border border-stone-200 bg-stone-50 px-3 py-1.5 text-[11.5px] text-stone-600">
                                <span>
                                  Entering rates manually. Want us to check the
                                  library instead?
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Flip back to library-driven matching.
                                    // Clears the manual matrix + re-fires the
                                    // matcher with the current name/region.
                                    setDraft((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            manualEntry: false,
                                            rateTable: undefined,
                                            matchStatus: "searching",
                                            matchedWbId: null,
                                            matchedWbName: null,
                                            candidates: [],
                                            libraryRates: null,
                                          }
                                        : prev,
                                    );
                                    queueMicrotask(() => fireMatch());
                                  }}
                                  className="rounded-md border border-stone-300 bg-white px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:bg-stone-100"
                                >
                                  Search library instead
                                </button>
                              </div>
                            )}
                          <RateTableEditor
                            mode={draft.rateMode ?? "tier_rates"}
                            rateTable={draft.rateTable}
                            internalPlanType={draft.internalPlanType}
                            onChange={(next) =>
                              setDraft((prev) =>
                                prev ? { ...prev, rateTable: next } : prev,
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                    )}

                    <details>
                      <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground">
                        Monthly contributions by tier (optional)
                      </summary>
                      <p className="mt-2 text-[11px] text-muted-foreground">
                        Enter <span className="font-medium">ER %</span> to fill that
                        tier's dollars from its rate (each tier can differ), or type
                        the dollars directly.
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                        {TIERS.map((t) => {
                          const c = (draft.contributions ?? []).find(
                            (x) => x.tier === t.key,
                          );
                          const tierRate = tierMonthlyRate(t.key);
                          const erPct =
                            tierRate && tierRate > 0 && c?.employer != null
                              ? String(
                                  Number(((c.employer / tierRate) * 100).toFixed(2)),
                                )
                              : "";
                          return (
                            <div key={t.key} className="space-y-2">
                              <Label className="text-xs text-muted-foreground">
                                {t.label}
                              </Label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                                  ER %
                                </span>
                                <Input
                                  type="number"
                                  step="1"
                                  min="0"
                                  max="100"
                                  className="h-8 pl-10 text-xs"
                                  value={erPct}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v.trim() === "") return;
                                    const pct = Number(v);
                                    if (Number.isFinite(pct) && pct >= 0 && pct <= 100) {
                                      applyEmployerPctToTier(t.key, pct);
                                    }
                                  }}
                                  placeholder={tierRate == null ? "rate first" : "%"}
                                  disabled={tierRate == null}
                                />
                              </div>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                                  ER $
                                </span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="h-8 pl-10 text-xs"
                                  value={c?.employer ?? ""}
                                  onChange={(e) =>
                                    updateDraftContribution(
                                      t.key,
                                      "employer",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="0.00"
                                />
                              </div>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                                  EE $
                                </span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="h-8 pl-10 text-xs"
                                  value={c?.employee ?? ""}
                                  onChange={(e) =>
                                    updateDraftContribution(
                                      t.key,
                                      "employee",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  </div>
                  );
                })()}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isEditing && (
        <div className="pt-2 text-xs text-muted-foreground">
          Save or cancel the card being edited first.
        </div>
      )}
    </div>
  );
}

function DisplayRow({
  label,
  value,
  required,
}: {
  label: string;
  value: string | null | undefined;
  required?: boolean;
}) {
  const missing = required && (!value || value.trim() === "" || value === "—");
  return (
    <div className="flex gap-2 border-b py-1 last:border-b-0">
      <span className="w-36 shrink-0 text-muted-foreground">{label}</span>
      <span
        className={`flex-1 ${missing ? "text-amber-700" : ""}`}
      >
        {value && value.trim() !== "" ? value : missing ? "(required)" : "—"}
      </span>
    </div>
  );
}
